import {useFetchSingle} from "../../hooks/useFetchSingle";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {faArrowLeft, faFile, faFileCode, faFileImage} from "@fortawesome/free-solid-svg-icons";
import IconButton from "../../components/IconButton";
import Heading from "../../components/Heading";
import {Customization, Page} from "../../types/Customization";
import Loader from "../../components/Loader";
import Card from "../../components/Card";
import {useTranslation} from "react-i18next";
import cx from "classnames";
import styles from './CustomizationDetail.module.css';
import Icon from "../../components/Icon";
import {ButtonSetting} from "../../types/ButtonSetting";

const CustomizationDetail: React.FC<{
    customization: [number, string]
    showCustomization: (customization: [number, string]|null) => void
    buttonSetting: ButtonSetting|null
}> = ({customization, showCustomization, buttonSetting}) => {
    const [activePage, setActivePage] = useState<Page|null>(null)
    const { t } = useTranslation(['orders', 'customizer'])

    const {
        data,
        fetch: fetchCustomization,
        isLoading,
    } = useFetchSingle<Customization>('customizations')

    const {
        data: fontData,
        fetch: fetchFonts,
    } = useFetchSingle<{ id: number; name: string; path: string }[]>('fonts');

    useEffect(() => {
        fetchCustomization(customization[0]);
        fetchFonts();
    }, [customization, fetchCustomization, fetchFonts]);

    useEffect(() => {
        if (data && data.pages.length > 0) {
            setActivePage(data.pages[0])
        }
    }, [data])

    const changePageByTemplate = useCallback((template_id: number) => {
        const page = data?.pages.find((p) => p.template_id === template_id)
        if (page) setActivePage(page)
    }, [data])

    const activeTemplate = useMemo(() => {
        if (activePage && data) {
            return data.templates.find((t) => t.id === activePage.template_id)
        }
    }, [activePage, data])

    const getSpotName = useCallback((spot_id: number) => {
        return activeTemplate?.spots.find((s) => s.id === spot_id)?.name
    }, [activeTemplate])

    const downloadModifiedSVG = async (svgUrl: string, filename: string, fontData: { name: string; path: string }[]) => {
        try {
            const response = await fetch(svgUrl);
            let svgText = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(svgText, "image/svg+xml");

            let defs = doc.querySelector("defs");
            if (!defs) {
                defs = doc.createElementNS("http://www.w3.org/2000/svg", "defs");
                doc.documentElement.prepend(defs);
            }

            const styleElement = doc.createElementNS("http://www.w3.org/2000/svg", "style");
            styleElement.setAttribute("type", "text/css");

            const usedFonts =  (data?.inputs ?? []).map((input) => {
                return input.font_family;
            });

            const fontFaceCSS = fontData
                .filter(font => font.path && usedFonts.includes(font.name))
                .map(font => `
                @font-face {
                    font-family: '${font.name}';
                    src: url('${font.path.startsWith("http") ? font.path : "/plugins" + font.path}');
                }
            `).join("\n");

            styleElement.innerHTML = fontFaceCSS;
            defs.appendChild(styleElement);

            const images = doc.querySelectorAll("image");
            for (let image of images) {
                const href = image.hasAttribute("xlink:href")
                    ? image.getAttribute("xlink:href")
                    : image.getAttribute("href");
                const attr = image.hasAttribute("xlink:href") ? "xlink:href" : "href";
                if (href && !href.startsWith("data:")) {
                    const imgResponse = await fetch(href);
                    const imgBlob = await imgResponse.blob();
                    const reader = new FileReader();
                    reader.readAsDataURL(imgBlob);

                    const base64String = await new Promise<string>((resolve) => {
                        reader.onloadend = () => resolve(reader.result as string);
                    });
                    image.setAttribute(attr, base64String);
                }
            }

            svgText = new XMLSerializer().serializeToString(doc.documentElement);
            const blob = new Blob([svgText], { type: "image/svg+xml" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            link.click();
        } catch (e) {
            console.error("Error downloading modified SVG:", e);
        }
    };



    const downloadModifiedSVGnoBG = async (svgUrl: string, filename: string, fontData: { name: string; path: string }[]) => {
        try {
            const response = await fetch(svgUrl);
            let svgText = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(svgText, "image/svg+xml");


            let defs = doc.querySelector("defs");
            if (!defs) {
                defs = doc.createElementNS("http://www.w3.org/2000/svg", "defs");
                doc.documentElement.prepend(defs);
            }


            const styleElement = doc.createElementNS("http://www.w3.org/2000/svg", "style");
            styleElement.setAttribute("type", "text/css");


            const fontFaceCSS = fontData
                .filter(font => font.path)
                .map(font => `
                @font-face {
                    font-family: '${font.name}';
                    src: url('${font.path.startsWith("http") ? font.path : "/plugins" + font.path}');
                }
            `).join("\n");


            styleElement.textContent = fontFaceCSS;
            defs.appendChild(styleElement);

            const backgroundGroup = doc.querySelector('g[clip-path*="CLIPPATH"]');
            const allImages = doc.querySelectorAll("image");

            allImages.forEach((image) => {
                if (!backgroundGroup || !backgroundGroup.contains(image)) {
                    image.remove();
                }
            });

            // Serialize the modified SVG with embedded fonts
            svgText = new XMLSerializer().serializeToString(doc.documentElement);
            const blob = new Blob([svgText], { type: "image/svg+xml" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            link.click();
        } catch (e) {
            console.error("Error downloading SVG without background:", e);
        }
    };




            return (
        <>
            <IconButton
                icon={faArrowLeft}
                color={"black"}
                onClick={() => showCustomization(null)}
            >
                {t('backToOverview')}
            </IconButton>
            <div className={'mt-3 mb-4'}>
                <div className={'d-flex w-100 align-items-center justify-content-between'}>
                    <Heading level={3}>{customization[1]}</Heading>
                    <div className={cx(styles['dimensions'], 'd-flex my-3')}>
                        {data?.templates.map((template) => {
                            return (
                                <div
                                    className={cx(styles['template-item'])}
                                    style={{
                                    background: activeTemplate?.id === template.id ? '#ccc' : '#f1f1f1',
                                }} onClick={() => changePageByTemplate(template.id)}>
                                    {template.name}
                                </div>
                            )
                        })}
                    </div>
                </div>
                {isLoading &&
                    <div className={'d-flex w-100 justify-content-center'}>
                        <Loader size={'m'} isLoading={isLoading}/>
                    </div>
                }

                {(activePage && activeTemplate) &&
                    <div className={'d-flex'}>
                        <div
                            className={cx(styles['page-container'], 'position-relative w-50 d-flex flex-column justify-content-center align-items-center')}
                        >
                            <div className={cx(styles['c-buttons-wrapper'])}>
                                <a href={activePage.path} download={activeTemplate.name}
                                   className={cx(styles['c-save-wrapper'])}
                                   style={{background: buttonSetting?.background_color ?? '#000'}}>
                                    <Icon icon={faFileImage} color={buttonSetting?.text_color ?? '#2d2d2d'}
                                          size={"1.8rem"}/>
                                </a>
                                <a
                                    onClick={() => downloadModifiedSVG(activePage.svg_path, `${activeTemplate.name}.svg`, fontData || [])}
                                    className={cx(styles['c-save-wrapper'])}
                                    style={{background: buttonSetting?.background_color ?? '#000'}}
                                >
                                    <Icon icon={faFileCode} color={buttonSetting?.text_color ?? '#2d2d2d'}
                                          size={"1.8rem"}/>
                                </a>
                                <a
                                    onClick={() => downloadModifiedSVGnoBG(activePage.svg_path, `${activeTemplate.name}.svg`, fontData || [])}
                                    className={cx(styles['c-save-wrapper'])}
                                    style={{background: buttonSetting?.background_color ?? '#000'}}
                                >
                                    <Icon icon={faFile} color={buttonSetting?.text_color ?? '#2d2d2d'}
                                          size={"1.8rem"}/>
                                </a>
                            </div>
                            <img src={activePage.path} alt={activeTemplate.name}/>
                        </div>
                        <div
                            className={cx(styles['details-container'], 'w-50 d-flex flex-column')}
                        >
                            <Heading level={3}>{activeTemplate.name}</Heading>
                            {data?.inputs.map((i) => {
                                return activeTemplate.spots.map((s) => s.id).includes(i.spot_id) ? (
                                    <Card key={i.id} classes={cx(styles['card'])} title={getSpotName(i.spot_id)}>
                                        <div className={'d-flex flex-column mt-3 ml-3'}>
                                            <small style={{color: '#555'}}>{t("content")}</small>
                                            {(i.type === 'text' || i.type === 'auto text')
                                                ? <span>{i.value}</span>
                                                : <a href={i.value} download={true}> {i.label}</a>
                                            }
                                        </div>
                                        {(i.type === 'text' || i.type === 'auto text') &&
                                            <div className={cx(styles['dimensions'], 'd-flex flex-row m-3 justify-content-between')}>
                                                <div className={'d-flex flex-column'}>
                                                    <small className={cx(styles['small'])}>{t('font_family')}</small>
                                                    <span>{i.font_family}</span>
                                                </div>
                                                <div className={'d-flex flex-column'}>
                                                    <small className={cx(styles['small'])}>{t('font_size')}</small>
                                                    <span>{i.font_size}</span>
                                                </div>
                                                <div className={'d-flex flex-column'}>
                                                    <small className={cx(styles['small'])}>{t('text_color')}</small>
                                                    <span>{i.text_color}</span>
                                                </div>
                                                <div className={'d-flex flex-column'}>
                                                    <small className={cx(styles['small'])}>{t('text_align')}</small>
                                                    <span>{i.text_align}</span>
                                                </div>
                                            </div>
                                        }
                                        <div className={cx(styles['dimensions'], 'd-flex flex-row m-3 justify-content-between')}>
                                            <div className={'d-flex flex-column'}>
                                                <small className={cx(styles['small'])}>{t('size')}</small>
                                                <span>{i.size}</span>
                                            </div>
                                            <div className={'d-flex flex-column'}>
                                                <small className={cx(styles['small'])}>{t('top')}</small>
                                                <span>{i.top}</span>
                                            </div>
                                            <div className={'d-flex flex-column'}>
                                                <small className={cx(styles['small'])}>{t('left')}</small>
                                                <span>{i.left}</span>
                                            </div>
                                            <div className={'d-flex flex-column'}>
                                                <small className={cx(styles['small'])}>{t('width')}</small>
                                                <span>{(i.width * i.scaleX).toFixed(2)}</span>
                                            </div>
                                            <div className={'d-flex flex-column'}>
                                                <small className={cx(styles['small'])}>{t('height')}</small>
                                                <span>{(i.height * i.scaleY).toFixed(2)}</span>
                                            </div>
                                            <div className={'d-flex flex-column'}>
                                                <small className={cx(styles['small'])}>{t('angle')}</small>
                                                <span>{i.angle}</span>
                                            </div>
                                        </div>
                                    </Card>
                                ) : null
                            })}
                        </div>
                    </div>
                }
            </div>
        </>
    )
}

export default CustomizationDetail
